import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { showAlert } from 'services/alerts.service'
import ActionCardComponent from 'components/common/ActionCardComponent'
import PageLoadingState from 'components/common/PageLoadingState'
import { systemRequest, getRequestErrorMessage } from 'components/System/systemRequest'
import Filters from 'components/System/scanRejections/Filters'
import Kpis from 'components/System/scanRejections/Kpis'
import Charts from 'components/System/scanRejections/Charts'
import PrefixTables from 'components/System/scanRejections/PrefixTables'
import RejectionsTable from 'components/System/scanRejections/RejectionsTable'
import { daysAgoLocal, todayLocal, toQueryString } from 'components/System/scanRejections/format'

const PAGE_SIZE = 500

const initialFilters = () => ({
  farmId: '',
  dateFrom: daysAgoLocal(6),
  dateTo: todayLocal(),
  source: '',
  reason: '',
  includeRepeatReads: false
})

// Sticker scans gf-app rejected ("already scanned", "not found"), across every
// tenant: where they concentrate (prefix, hour, day) and the evidence of why
// (batch the app resolved vs the real one, reset window, the scan that blocked it).
const ScanRejections = () => {
  const { t } = useTranslation()
  const [tenants, setTenants] = useState([])
  const [filters, setFilters] = useState(initialFilters)
  const [summary, setSummary] = useState(null)
  const [loadingSummary, setLoadingSummary] = useState(false)
  // The detail can be narrowed to one prefix (clicked in a chart or table),
  // which also pins its tenant: with every tenant shown, two farms can share it.
  const [detailScope, setDetailScope] = useState({ prefix: null, farmId: null, offset: 0 })
  const [page, setPage] = useState(null)
  const [loadingPage, setLoadingPage] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  useEffect(() => {
    systemRequest('GET', '/system/tenants')
      .then(response => setTenants(response.data.tenants || []))
      .catch(error => console.error('Error fetching tenants:', error))
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoadingSummary(true)
    systemRequest('GET', `/system/scan-rejections/summary?${toQueryString(filters)}`)
      .then(response => { if (!cancelled) setSummary(response.data) })
      .catch(error => {
        if (!cancelled) showAlert({ message: getRequestErrorMessage(error, t('system.scanRejections.fetchError')), icon: 'error' })
      })
      .finally(() => { if (!cancelled) setLoadingSummary(false) })
    return () => { cancelled = true }
  }, [filters, refreshKey])

  useEffect(() => {
    let cancelled = false
    setLoadingPage(true)
    const query = toQueryString({
      ...filters,
      farmId: detailScope.farmId || filters.farmId,
      stickerPrefix: detailScope.prefix,
      limit: PAGE_SIZE,
      offset: detailScope.offset
    })
    systemRequest('GET', `/system/scan-rejections?${query}`)
      .then(response => { if (!cancelled) setPage(response.data) })
      .catch(error => {
        if (!cancelled) showAlert({ message: getRequestErrorMessage(error, t('system.scanRejections.fetchError')), icon: 'error' })
      })
      .finally(() => { if (!cancelled) setLoadingPage(false) })
    return () => { cancelled = true }
  }, [filters, detailScope, refreshKey])

  const handleFilterChange = changes => {
    setFilters(f => ({ ...f, ...changes }))
    setDetailScope(scope => ({ ...scope, offset: 0 }))
  }

  const handlePrefixClick = useCallback(row => {
    // Rejections without a resolvable prefix can't be filtered by it.
    if (!row?.prefix) return
    setDetailScope({ prefix: row.prefix, farmId: row.farmId, offset: 0 })
  }, [])

  return (
    <div className='flex flex-col gap-6'>
      <ActionCardComponent>
        <div data-align='left' className='flex flex-col'>
          <h3 className='text-lg font-semibold text-greenPrimary'>{t('system.scanRejections.title')}</h3>
          <span className='text-sm text-gray-500'>{t('system.scanRejections.subtitle')}</span>
        </div>
      </ActionCardComponent>

      <Filters
        filters={filters}
        tenants={tenants}
        loading={loadingSummary || loadingPage}
        onChange={handleFilterChange}
        onRefresh={() => setRefreshKey(key => key + 1)}
      />

      {!summary && loadingSummary
        ? <PageLoadingState />
        : (
          <>
            <Kpis totals={summary?.totals} />
            <Charts summary={summary} onPrefixClick={handlePrefixClick} />
            <PrefixTables
              byPrefix={summary?.byPrefix || []}
              prefixCollisions={summary?.prefixCollisions || []}
              onPrefixClick={handlePrefixClick}
            />
          </>
          )}

      <RejectionsTable
        page={page}
        prefixFilter={detailScope.prefix}
        loading={loadingPage}
        onClearPrefix={() => setDetailScope({ prefix: null, farmId: null, offset: 0 })}
        onPageChange={offset => setDetailScope(scope => ({ ...scope, offset }))}
      />
    </div>
  )
}

export default ScanRejections
